import React from "react";

import ToolTipBase from "@/components/ui/tooltip";
import {
  CHART_AXIS_FONT,
  getYAxisWidth,
  wrapLabel
} from "@/helpers/chartHelper";
import { compactNumber, formatNumber } from "@/utils/number";

import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  ResponsiveContainerProps,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import ChartLegend from "./ChartLegend";
import { ChartTooltipContent } from "./GroupedChartTooltip";
import {
  buildSeriesFromData,
  ChartSeries,
  domainTickCalculate,
  resolveCategoryKey
} from "./utils";

interface HorizontalBarChartProps
  extends Omit<ResponsiveContainerProps, "children"> {
  data: Record<string, string | number>[];
  /** Key used as Y-axis category label. Auto-detected if omitted. */
  categoryKey?: string;
  /** One entry per bar group. Auto-built from data keys if omitted. */
  series?: ChartSeries[];
  title?: string;
  onBarClick?: (name: string) => void;
  /** Aggregation appended to the legend label when the chart is single-series. */
  aggregation?: string;
}

// Height reserved for the pinned X-axis strip below the scroll area.
const AXIS_STRIP_HEIGHT = 30;

// Y-axis label sizing. The axis width grows to fit the longest label but is
// capped at MAX_Y_AXIS_WIDTH; labels wider than that wrap onto up to two lines.
const MAX_Y_AXIS_WIDTH = 100;
const Y_AXIS_H_PADDING = 16;
const Y_AXIS_LINE_HEIGHT = 14;
const Y_AXIS_MAX_LINES = 2;

interface AxisTickProps {
  x: number;
  y: number;
  payload: { value: string | number };
}

// Renders a Y-axis label wrapped onto up to two lines to fit `maxTextWidth`;
// a truncated label shows its full text in a tooltip on hover.
const createYAxisTick =
  (maxTextWidth: number) =>
  ({ x, y, payload }: AxisTickProps) => {
    const fullLabel = String(payload.value);
    const lines = wrapLabel(
      fullLabel,
      maxTextWidth,
      CHART_AXIS_FONT,
      Y_AXIS_MAX_LINES
    );
    const isTruncated = lines.length > 1 || lines[0] !== fullLabel;
    const startDy = 4 - ((lines.length - 1) * Y_AXIS_LINE_HEIGHT) / 2;

    const label = (
      <text
        x={0}
        y={0}
        textAnchor="end"
        fill="#000000B2"
        fontSize={12}
        style={{ cursor: isTruncated ? "pointer" : "default" }}
      >
        {lines.map((line, index) => (
          <tspan
            key={line}
            x={0}
            dy={index === 0 ? startDy : Y_AXIS_LINE_HEIGHT}
          >
            {line}
          </tspan>
        ))}
      </text>
    );

    return (
      <g transform={`translate(${x + 5},${y})`}>
        {isTruncated ? (
          <ToolTipBase content={fullLabel} position="top-center">
            {label}
          </ToolTipBase>
        ) : (
          label
        )}
      </g>
    );
  };

const HorizontalBarChart = React.forwardRef<
  HTMLDivElement,
  HorizontalBarChartProps
>((props, ref) => {
  const {
    data,
    categoryKey: categoryKeyProp,
    series: seriesProp,
    title,
    onBarClick,
    height,
    aggregation,
    ...rest
  } = props;

  const categoryKey = React.useMemo(
    () => resolveCategoryKey(data, categoryKeyProp),
    [categoryKeyProp, data]
  );

  const series = React.useMemo(
    () => seriesProp ?? buildSeriesFromData(data, categoryKey),
    [seriesProp, data, categoryKey]
  );

  const isGrouped = series.length > 1;

  const numericValues = React.useMemo(
    () =>
      data
        .flatMap((item) => series.map((s) => Number(item[s.dataKey])))
        .filter((v) => !Number.isNaN(v)),
    [data, series]
  );

  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = React.useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  const totalValue = numericValues.reduce((sum, v) => sum + v, 0);

  const labelData = data.map((item) => ({ name: String(item[categoryKey]) }));
  // Text area grows to fit the widest label but is capped; the axis width is
  // derived from it so longer labels wrap instead of widening the axis.
  const labelTextWidth = Math.min(
    Math.ceil(getYAxisWidth(labelData, CHART_AXIS_FONT)),
    MAX_Y_AXIS_WIDTH - Y_AXIS_H_PADDING
  );
  const yAxisWidth = labelTextWidth + Y_AXIS_H_PADDING;
  const yAxisTick = React.useMemo(
    () => createYAxisTick(labelTextWidth),
    [labelTextWidth]
  );

  // Tighten the spacing between bars within a category for grouped charts with
  // few series (< 5); otherwise keep Recharts' default gap of 4.
  const barGap = isGrouped && series.length < 5 ? -20 : 4;

  const viewportHeight = Number(height) || 600;

  const bars = React.useMemo(() => {
    const barSize = isGrouped ? 10 : 30;
    const barRadius: [number, number, number, number] = [
      0,
      isGrouped ? 10 : 999,
      isGrouped ? 10 : 999,
      0
    ];
    return series.map((s) => (
      <Bar
        key={s.dataKey}
        dataKey={s.dataKey}
        name={s.label ?? s.dataKey}
        fill={s.color}
        maxBarSize={barSize}
        radius={barRadius}
        onClick={(data) => onBarClick?.(String(data[categoryKey]))}
      >
        {!isGrouped && (
          <LabelList
            dataKey={s.dataKey}
            position="right"
            fill="#000000B2"
            fontWeight={700}
            fontSize={12}
            formatter={(value: number | string) =>
              String(formatNumber(Number(value), "decimal"))
            }
          />
        )}
      </Bar>
    ));
  }, [series, isGrouped, categoryKey, onBarClick]);

  const tooltip = (
    <Tooltip
      content={
        <ChartTooltipContent isGrouped={isGrouped} totalValue={totalValue} />
      }
    />
  );

  // Single-series: original layout — X-axis and legend live inside one
  // non-scrolling chart.
  if (!isGrouped) {
    return (
      <div
        ref={ref}
        className="w-full flex flex-col"
        style={{ height: viewportHeight }}
      >
        <div className="flex-1 min-h-0">
          <ResponsiveContainer {...rest} width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              barCategoryGap="15%"
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
              aria-label={title}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, axisDomainMax]}
                ticks={axisTicks}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                fontSize={12}
              />
              <YAxis
                dataKey={categoryKey}
                type="category"
                className="text-[#000000]/70"
                tickMargin={10}
                width={yAxisWidth}
                tick={yAxisTick}
              />
              {tooltip}
              {bars}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend
          series={series}
          iconType="rect"
          aggregation={aggregation}
        />
      </div>
    );
  }

  // Grouped: bars scroll vertically while the X-axis stays pinned below.
  const rowHeight = Math.max(56, series.length * 16);
  const scrollViewport = viewportHeight - AXIS_STRIP_HEIGHT;
  const contentHeight = data.length * rowHeight + 10;
  // Grow to fit all rows, but never shrink below the scroll viewport (avoids
  // scroll for small datasets while keeping the chart from collapsing).
  const barsHeight = Math.max(contentHeight, scrollViewport);

  // Shared horizontal margins so the pinned axis lines up with the scrolling
  // bars (identical left/right + reserved Y-axis width = identical plot area).
  const chartMargin = { top: 5, right: 30, left: 0, bottom: 0 };

  return (
    <div
      ref={ref}
      className="w-full flex flex-col"
      style={{ height: viewportHeight }}
    >
      {/* Scrolling bar area — the X-axis is hidden here and pinned below. */}
      <div className="flex-1 overflow-y-auto">
        <ResponsiveContainer {...rest} width="100%" height={barsHeight}>
          <BarChart
            data={data}
            layout="vertical"
            barCategoryGap="15%"
            barGap={barGap}
            margin={chartMargin}
            aria-label={title}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, axisDomainMax]}
              ticks={axisTicks}
              allowDecimals={false}
              hide
            />
            <YAxis
              dataKey={categoryKey}
              type="category"
              className="text-[#000000]/70"
              tickMargin={10}
              width={yAxisWidth}
              tick={yAxisTick}
            />
            {tooltip}
            {bars}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pinned X-axis — an empty chart that renders only the numeric axis,
          with the Y-axis width reserved so its ticks align with the bars. */}
      <div className="shrink-0" style={{ height: AXIS_STRIP_HEIGHT }}>
        <ResponsiveContainer width="100%" height={AXIS_STRIP_HEIGHT}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ ...chartMargin, top: 0, bottom: 5 }}
          >
            <XAxis
              type="number"
              domain={[0, axisDomainMax]}
              ticks={axisTicks}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              fontSize={12}
              tickFormatter={(v) => compactNumber(v)}
            />
            <YAxis
              dataKey={categoryKey}
              type="category"
              width={yAxisWidth}
              tick={false}
              tickLine={false}
              axisLine={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ChartLegend series={series} iconType="rect" aggregation={aggregation} />
    </div>
  );
});

HorizontalBarChart.displayName = "HorizontalBarChart";

export default HorizontalBarChart;
